import React, { useState } from 'react';
import { descriptionBullets } from '../../utils/trackingLinks';
import DescriptionItemsInput from './DescriptionItemsInput';
import IncludedItemsModal from './IncludedItemsModal';
import * as ds from '../../constants/dashboardStyles';

const styles = {
  card: ds.card,
  title: ds.cardTitle,
  description: ds.cardDescription,
  fieldLabel: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#555',
    margin: '16px 0 6px',
  },
  fieldLabelFirst: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#555',
    margin: '0 0 6px',
  },
  fieldHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '16px 0 6px',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontWeight: 400,
    fontSize: '0.75rem',
    color: '#888',
    cursor: 'pointer',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  row: {
    display: 'flex',
    gap: 8,
  },
  senderRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 8,
  },
  senderButton: {
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    background: '#fff',
    color: '#555',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  senderButtonActive: {
    background: '#111',
    color: '#fff',
    border: '1px solid #111',
  },
  priceInputWrap: {
    position: 'relative',
    flex: 1,
  },
  priceSymbol: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#888',
    fontSize: '0.9rem',
    pointerEvents: 'none',
  },
  priceInput: {
    width: '100%',
    padding: '10px 12px 10px 26px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
  },
  messageBlock: {
    marginTop: 24,
    paddingTop: 20,
    borderTop: '1px solid #f0f0f0',
  },
  messageLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  messageLabelText: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#555',
  },
  preview: {
    whiteSpace: 'pre-wrap',
    background: '#f6f6f4',
    border: '1px solid #ececE6',
    borderRadius: 10,
    padding: '14px 16px',
    fontSize: '0.87rem',
    color: '#333',
    lineHeight: 1.6,
  },
  copyButton: {
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    fontSize: '0.8rem',
    cursor: 'pointer',
  },
  messageToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  chevron: {
    color: '#999',
    display: 'flex',
    transition: 'transform 0.2s ease',
  },
  presetsButton: {
    width: '100%',
    marginTop: 8,
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px dashed #c5c5cc',
    background: '#fafafa',
    color: '#555',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      style={styles.copyButton}
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function ChevronIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.5"
         strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function MessageBlock({ label, text }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.messageBlock}>
      <div style={styles.messageLabel}>
        <button type="button" style={styles.messageToggle} onClick={() => setOpen((o) => !o)}>
          <span style={styles.messageLabelText}>{label}</span>
          <span style={{ ...styles.chevron, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <ChevronIcon />
          </span>
        </button>
        <CopyButton text={text} />
      </div>
      <div style={{
        maxHeight: open ? 500 : 0,
        opacity: open ? 1 : 0,
        marginTop: open ? 8 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.3s ease, opacity 0.2s ease, margin-top 0.3s ease',
      }}>
        <div style={styles.preview}>{text}</div>
      </div>
    </div>
  );
}

export default function OutreachPanel() {
  const [businessName, setBusinessName] = useState('');
  const [senderName, setSenderName] = useState('David');
  const [trackingLink, setTrackingLink] = useState('');
  const [personalNote, setPersonalNote] = useState('');
  const [features, setFeatures] = useState('');
  const [quotePrice, setQuotePrice] = useState('');
  const [monthlyPrice, setMonthlyPrice] = useState('');
  const [discountPeriod, setDiscountPeriod] = useState('1 year');
  const [includedItemsModalOpen, setIncludedItemsModalOpen] = useState(false);
  const [showPersonalNote, setShowPersonalNote] = useState(true);
  const [showOffer, setShowOffer] = useState(true);

  const applyIncludedItems = (items) => {
    setFeatures((prev) => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed}\n${items.join('\n')}` : items.join('\n');
    });
  };

  const bn = businessName.trim() || '[Business name]';
  const sn = senderName.trim() || '[Your name]';
  const link = trackingLink.trim();
  const note = showPersonalNote ? personalNote.trim() : '';
  const qp = quotePrice.trim() || '[price]';
  const mp = monthlyPrice.trim() || '[monthly price]';
  const dp = discountPeriod.trim() || '[period]';
  const featureList = showOffer ? descriptionBullets(features) : [];
  const complimentLine = note ? `We love your posts ${note}` : `We love your posts!`;

  const message1 = [
    `Hey ${bn}! I'm ${sn} from DDHAPPS👋`,
    complimentLine,
    `I noticed you don't have a website linked in your bio. Have you ever thought about getting one set up? It could make it much easier for people to find your menu, location and other info through Google and AI search.`,
  ].join('\n\n');

  const message2 = [
    `We specialise in building websites for businesses like yours.`,
    `After taking a look through your social media pages, we've put together a few ideas for what your website could look like.`,
    ...(showOffer ? [`We think it'd be great if you had:\n${featureList.map((f) => `• ${f}`).join('\n')}`] : []),
    ...(showOffer ? [`With this in mind, we've initially quoted it at £${qp}, with £${mp}* a month in maintenance for ${dp}* at our discounted rate.`] : []),
    ...(link ? [`You can view your personalised quote here: ${link}`] : []),
  ].join('\n\n');

  return (
    <div style={styles.card}>
      <div style={styles.title}>Outreach messages</div>
      <p style={styles.description}>
        Fill in what you know — anything left blank shows as a placeholder you can edit after pasting. Built for DMing prospects on Instagram, Facebook, Twitter, etc.
      </p>

      <div style={styles.fieldLabelFirst}>Business name</div>
      <input
        style={styles.input}
        type="text"
        placeholder="e.g. The Coffee House"
        value={businessName}
        onChange={(e) => setBusinessName(e.target.value)}
      />

      <div style={styles.fieldLabel}>Tracking link <span style={{ fontWeight: 400, color: '#999' }}>Optional</span></div>
      <input
        style={styles.input}
        type="text"
        placeholder="Paste their tracking link"
        value={trackingLink}
        onChange={(e) => setTrackingLink(e.target.value)}
      />

      <div style={styles.fieldLabel}>From</div>
      <div style={styles.senderRow}>
        {['David', 'Hugo'].map((n) => (
          <button
            key={n}
            type="button"
            style={{ ...styles.senderButton, ...(senderName === n ? styles.senderButtonActive : {}) }}
            onClick={() => setSenderName(n)}
          >
            {n}
          </button>
        ))}
      </div>

      <div style={styles.fieldHeader}>
        <span>Personal note <span style={{ fontWeight: 400, color: '#999' }}>Optional</span></span>
        <label style={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={showPersonalNote}
            onChange={(e) => setShowPersonalNote(e.target.checked)}
          />
          Include
        </label>
      </div>
      {showPersonalNote && (
        <input
          style={styles.input}
          type="text"
          placeholder="Finishes 'We love your posts...', e.g. and the coffee looks amazing!"
          value={personalNote}
          onChange={(e) => setPersonalNote(e.target.value)}
        />
      )}

      <div style={styles.fieldHeader}>
        <span>What we'd offer them & pricing</span>
        <label style={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={showOffer}
            onChange={(e) => setShowOffer(e.target.checked)}
          />
          Include
        </label>
      </div>
      {showOffer && (
        <>
          <DescriptionItemsInput value={features} onChange={setFeatures} placeholder="e.g. Monthly promotions" />
          <button type="button" style={styles.presetsButton} onClick={() => setIncludedItemsModalOpen(true)}>
            + Add included items
          </button>

          {includedItemsModalOpen && (
            <IncludedItemsModal
              existingDescription={features}
              onApply={applyIncludedItems}
              onClose={() => setIncludedItemsModalOpen(false)}
            />
          )}

          <div style={styles.fieldLabel}>Pricing</div>
          <div style={styles.row}>
            <div style={styles.priceInputWrap}>
              <span style={styles.priceSymbol}>£</span>
              <input
                style={styles.priceInput}
                type="text"
                placeholder="400"
                value={quotePrice}
                onChange={(e) => setQuotePrice(e.target.value)}
              />
            </div>
            <div style={styles.priceInputWrap}>
              <span style={styles.priceSymbol}>£</span>
              <input
                style={styles.priceInput}
                type="text"
                placeholder="20"
                value={monthlyPrice}
                onChange={(e) => setMonthlyPrice(e.target.value)}
              />
            </div>
            <input
              style={{ ...styles.input, flex: 1 }}
              type="text"
              placeholder="1 year"
              value={discountPeriod}
              onChange={(e) => setDiscountPeriod(e.target.value)}
            />
          </div>
        </>
      )}

      <MessageBlock label="Message 1 — the opener" text={message1} />
      <MessageBlock label="Message 2 — the pitch" text={message2} />
    </div>
  );
}
